import { useEffect, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { groupApi, teamApi } from '@/services/api';
import { SingleSelectDropdown } from '@/components/ui/SingleSelectDropdown';

interface Group {
  id: string;
  name: string;
  description?: string | null;
  displayOrder: number;
  teams?: { id: string; name: string }[];
}

interface Team {
  id: string;
  name: string;
  description?: string | null;
  groupId?: string | null;
  group?: { id: string; name: string } | null;
  members?: { userId: string }[];
}

export function TeamsTab() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);
  const [editingTeamName, setEditingTeamName] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamGroupId, setNewTeamGroupId] = useState('');
  const [submittingGroup, setSubmittingGroup] = useState(false);
  const [submittingTeam, setSubmittingTeam] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [groupsData, teamsData] = await Promise.all([groupApi.getAll(), teamApi.getAll()]);
      setGroups(groupsData);
      setTeams(teamsData);
    } catch (err: any) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // --- Group handlers ---

  const handleAddGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    setSubmittingGroup(true);
    try {
      const g = await groupApi.create({ name: newGroupName.trim() });
      setGroups(prev => [...prev, g]);
      setNewGroupName('');
    } catch (err: any) {
      alert(err?.message || 'Failed to create group');
    } finally {
      setSubmittingGroup(false);
    }
  };

  const handleSaveGroupEdit = async (id: string) => {
    if (!editingGroupName.trim()) return;
    try {
      const updated = await groupApi.update(id, { name: editingGroupName.trim() });
      setGroups(prev => prev.map(g => g.id === id ? updated : g));
      setEditingGroupId(null);
      setEditingGroupName('');
    } catch (err: any) {
      alert(err?.message || 'Failed to update group');
    }
  };

  const handleDeleteGroup = async (id: string, name: string) => {
    const groupTeams = teams.filter(t => t.groupId === id);
    if (groupTeams.length > 0) {
      alert(`Cannot delete "${name}" — it still has ${groupTeams.length} team(s). Move or delete those teams first.`);
      return;
    }
    if (!confirm(`Delete group "${name}"?`)) return;
    try {
      await groupApi.delete(id);
      setGroups(prev => prev.filter(g => g.id !== id));
    } catch (err: any) {
      alert(err?.message || 'Failed to delete group');
    }
  };

  // --- Team handlers ---

  const handleAddTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeamName.trim() || !newTeamGroupId) return;
    setSubmittingTeam(true);
    try {
      const t = await teamApi.create({ name: newTeamName.trim(), groupId: newTeamGroupId });
      setTeams(prev => [...prev, t]);
      setNewTeamName('');
      setNewTeamGroupId('');
    } catch (err: any) {
      alert(err?.message || 'Failed to create team');
    } finally {
      setSubmittingTeam(false);
    }
  };

  const handleSaveTeamEdit = async (id: string) => {
    if (!editingTeamName.trim()) return;
    try {
      const updated = await teamApi.update(id, { name: editingTeamName.trim() });
      setTeams(prev => prev.map(t => t.id === id ? updated : t));
      setEditingTeamId(null);
      setEditingTeamName('');
    } catch (err: any) {
      alert(err?.message || 'Failed to update team');
    }
  };

  const handleReassignTeamGroup = async (id: string, value: string) => {
    try {
      const updated = await teamApi.update(id, { groupId: value || undefined });
      setTeams(prev => prev.map(t => t.id === id ? updated : t));
    } catch (err: any) {
      alert(err?.message || 'Failed to reassign team group');
      await load();
    }
  };

  const handleDeleteTeam = async (id: string, name: string, team: Team) => {
    // Client-side guard: only block when member data is loaded and non-empty.
    // The server also enforces this constraint as a fallback.
    if (team.members !== undefined && team.members.length > 0) {
      alert(`Cannot delete "${name}" — it still has ${team.members.length} member(s). Remove members first.`);
      return;
    }
    if (!confirm(`Delete team "${name}"?`)) return;
    try {
      await teamApi.delete(id);
      setTeams(prev => prev.filter(t => t.id !== id));
    } catch (err: any) {
      alert(err?.message || 'Failed to delete team');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-text-secondary">Loading...</div>;
  }

  if (error) {
    return (
      <div>
        <p className="text-red-600 text-sm">{error}</p>
        <button onClick={load} className="mt-2 text-sm text-blue-600 hover:underline">Retry</button>
      </div>
    );
  }

  const inputCls =
    'flex-1 text-sm border border-border-default rounded-md px-3 py-2 bg-background-primary text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500';

  // Group teams under their parent group; teams with no group fall into an "Ungrouped" bucket last.
  const teamsByGroup: { group: Group | null; groupTeams: Team[] }[] = [
    ...groups
      .map(g => ({ group: g as Group | null, groupTeams: teams.filter(t => t.groupId === g.id) }))
      .filter(x => x.groupTeams.length > 0),
  ];
  const ungrouped = teams.filter(t => !t.groupId);
  if (ungrouped.length) teamsByGroup.push({ group: null, groupTeams: ungrouped });

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-base font-semibold text-text-primary">Teams &amp; Groups</h2>
        <p className="text-sm text-text-secondary mt-1">
          Organize users into a <strong>Group → Team → User</strong> hierarchy. This powers sharing of saved views.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ===== LEFT COLUMN: Groups ===== */}
        <section className="border border-border-default rounded-md overflow-hidden">
          <div className="bg-blue-50/70 px-4 py-3 border-b border-border-default">
            <span className="text-sm font-semibold text-text-primary">Groups</span>
            <span className="ml-2 text-xs text-text-tertiary">Top level (grandparent)</span>
          </div>

          <form onSubmit={handleAddGroup} className="p-3 flex items-center gap-2 border-b border-border-subtle">
            <input
              required
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              placeholder="e.g. CSMO"
              className={inputCls}
            />
            <button
              type="submit"
              disabled={!newGroupName.trim() || submittingGroup}
              className="flex-shrink-0 flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <PlusIcon className="w-4 h-4" /> Add
            </button>
          </form>

          <div className="divide-y divide-border-subtle">
            {groups.length === 0 ? (
              <p className="px-4 py-3 text-sm text-text-tertiary">No groups yet.</p>
            ) : (
              groups.map(group => {
                const groupTeams = teams.filter(t => t.groupId === group.id);
                const groupTeamCount = groupTeams.length;
                const groupUserCount = groupTeams.reduce((sum, t) => sum + (t.members?.length ?? 0), 0);
                const cannotDeleteGroup = groupTeamCount > 0;
                const groupDeleteReason = groupUserCount > 0
                  ? "This group has users and can't be deleted"
                  : "This group has teams and can't be deleted";
                const isEditing = editingGroupId === group.id;
                return (
                  <div key={group.id} className="px-4 py-2.5 flex items-center gap-2">
                    {isEditing ? (
                      <>
                        <input
                          autoFocus
                          value={editingGroupName}
                          onChange={e => setEditingGroupName(e.target.value)}
                          className={inputCls}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); handleSaveGroupEdit(group.id); }
                            if (e.key === 'Escape') { setEditingGroupId(null); setEditingGroupName(''); }
                          }}
                        />
                        <button onClick={() => handleSaveGroupEdit(group.id)} className="p-1.5 text-green-600 hover:bg-surface-hover rounded" title="Save">
                          <CheckIcon className="w-4 h-4" />
                        </button>
                        <button onClick={() => { setEditingGroupId(null); setEditingGroupName(''); }} className="p-1.5 text-text-tertiary hover:bg-surface-hover rounded" title="Cancel">
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm text-text-primary truncate" title={group.name}>{group.name}</span>
                        <span className="text-xs text-text-tertiary whitespace-nowrap">{groupTeamCount} team{groupTeamCount !== 1 ? 's' : ''}</span>
                        <button
                          onClick={() => { setEditingGroupId(group.id); setEditingGroupName(group.name); }}
                          className="p-1.5 text-text-tertiary hover:text-text-secondary hover:bg-surface-hover rounded"
                          title="Rename"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <span title={cannotDeleteGroup ? groupDeleteReason : undefined} className="inline-flex">
                          <button
                            onClick={() => handleDeleteGroup(group.id, group.name)}
                            disabled={cannotDeleteGroup}
                            className="p-1.5 text-text-tertiary hover:text-red-500 hover:bg-surface-hover rounded disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-text-tertiary"
                            title={cannotDeleteGroup ? undefined : 'Delete'}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </span>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* ===== RIGHT COLUMN: Teams ===== */}
        <section className="border border-border-default rounded-md overflow-hidden">
          <div className="bg-blue-50/70 px-4 py-3 border-b border-border-default">
            <span className="text-sm font-semibold text-text-primary">Teams</span>
            <span className="ml-2 text-xs text-text-tertiary">Parent</span>
          </div>

          <form onSubmit={handleAddTeam} className="p-3 flex flex-col gap-3 border-b border-border-subtle">
            <input
              required
              value={newTeamName}
              onChange={e => setNewTeamName(e.target.value)}
              placeholder="e.g. Strategy"
              className={inputCls + ' w-full'}
            />
            <div className="flex items-end gap-2">
              <SingleSelectDropdown
                variant="field"
                fieldLabel="Group"
                required
                className="flex-1 min-w-0"
                value={newTeamGroupId}
                onChange={setNewTeamGroupId}
                placeholder="Select group"
                options={groups.map(g => ({ value: g.id, label: g.name }))}
              />
              <button
                type="submit"
                disabled={!newTeamName.trim() || !newTeamGroupId || submittingTeam}
                className="flex-shrink-0 flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                <PlusIcon className="w-4 h-4" /> Add
              </button>
            </div>
          </form>

          <div>
            {teams.length === 0 ? (
              <p className="px-4 py-3 text-sm text-text-tertiary">No teams yet.</p>
            ) : (
              teamsByGroup.map(({ group, groupTeams }) => (
                <div key={group?.id ?? 'ungrouped'}>
                  <div className="px-4 py-1.5 bg-gray-50 border-b border-border-subtle flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                      {group ? group.name : 'No group'}
                    </span>
                    <span className="text-xs text-text-tertiary">· {groupTeams.length}</span>
                  </div>
                  <div className="divide-y divide-border-subtle">
                    {groupTeams.map(team => {
                      const memberCount = team.members?.length ?? 0;
                      const hasKnownMembers = team.members !== undefined && team.members.length > 0;
                      const isEditing = editingTeamId === team.id;
                      return (
                        <div key={team.id} className="px-4 py-2.5 pl-6 flex items-center gap-2">
                          {isEditing ? (
                            <>
                              <input
                                autoFocus
                                value={editingTeamName}
                                onChange={e => setEditingTeamName(e.target.value)}
                                className={inputCls}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') { e.preventDefault(); handleSaveTeamEdit(team.id); }
                                  if (e.key === 'Escape') { setEditingTeamId(null); setEditingTeamName(''); }
                                }}
                              />
                              <button onClick={() => handleSaveTeamEdit(team.id)} className="p-1.5 text-green-600 hover:bg-surface-hover rounded" title="Save">
                                <CheckIcon className="w-4 h-4" />
                              </button>
                              <button onClick={() => { setEditingTeamId(null); setEditingTeamName(''); }} className="p-1.5 text-text-tertiary hover:bg-surface-hover rounded" title="Cancel">
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="flex-1 text-sm text-text-primary truncate" title={team.name}>{team.name}</span>
                              <SingleSelectDropdown
                                variant="inline"
                                className="w-44 flex-shrink-0"
                                align="right"
                                value={team.groupId || ''}
                                onChange={v => handleReassignTeamGroup(team.id, v)}
                                placeholder="No group"
                                options={groups.map(g => ({ value: g.id, label: g.name }))}
                              />
                              <button
                                onClick={() => { setEditingTeamId(team.id); setEditingTeamName(team.name); }}
                                className="p-1.5 text-text-tertiary hover:text-text-secondary hover:bg-surface-hover rounded"
                                title="Rename"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <span title={hasKnownMembers ? "This team has users and can't be deleted" : undefined} className="inline-flex">
                                <button
                                  onClick={() => handleDeleteTeam(team.id, team.name, team)}
                                  disabled={hasKnownMembers}
                                  className="p-1.5 text-text-tertiary hover:text-red-500 hover:bg-surface-hover rounded disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-text-tertiary"
                                  title={hasKnownMembers ? undefined : (memberCount > 0 ? 'Remove members first' : 'Delete')}
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </span>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

      </div>
    </div>
  );
}
